import React, { useMemo } from "react";
import { Combobox } from "@/components/ui";
import {
  ReviewFiltersPopoverLayout,
  ReviewSentimentFilterButtons,
  ReviewSourceFilterButtons,
  ReviewTriageFilterButtons,
  ReviewTimeFrameFilter,
} from "@/components/reviews";
import { useAccountSettings, useSegmentation } from "@/utils";
import { WebComponents } from "@/interfaces";
import { cn } from "@/lib/utils";
import { CoachRatingFilterFields } from "./CoachRatingFilterFields";
import { useCoachReviewsFilters } from "./CoachReviewsFiltersContext";

type ReviewsFiltersPopoverProps = {
  onClose: () => void;
  side?: "right" | "bottom";
  resultCount?: number | null;
};

export function ReviewsFiltersPopover({ onClose, side = "right", resultCount }: ReviewsFiltersPopoverProps) {
  const {
    players,
    playerId,
    searchInput,
    clearAllPopoverFilters,
    filters: state,
    segmentationIds,
    setSegmentationIds,
    setSourceSystem,
    setSentiment,
    setTriage,
    setDatePreset,
    setArchived,
    setRatingType,
    setRatingValue,
    setNpsCategory,
    setRecommendation,
  } = useCoachReviewsFilters();
  const { segmentationOptions } = useSegmentation(["REGION", "BRANCH"]);
  const { hasWritePermission } = useAccountSettings();
  const canSeeArchived = hasWritePermission(WebComponents.REPUTATION_MANAGEMENT);

  const selectedSegmentations = useMemo(
    () => segmentationOptions.filter(option => segmentationIds.includes(Number(option.value))),
    [segmentationOptions, segmentationIds]
  );

  const hasPopoverFilters = Boolean(
    searchInput.trim() ||
      playerId ||
      segmentationIds.length ||
      state.triage ||
      state.sourceSystem ||
      state.sentiment ||
      state.ratingType ||
      state.ratingValue != null ||
      state.npsCategory ||
      state.recommendation ||
      state.datePreset ||
      state.startDate ||
      state.endDate ||
      state.archived
  );

  return (
    <ReviewFiltersPopoverLayout
      side={side}
      initialFocus={false}
      onClear={hasPopoverFilters ? clearAllPopoverFilters : undefined}
      onDone={onClose}
      resultCount={resultCount}
    >
      <div className="flex flex-col gap-2">
        <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Player</label>
        <Combobox {...players} showClear placeholder="Filter by Player..." className="w-full bg-white" />
      </div>

      {segmentationOptions.length > 0 ? (
        <div className="flex flex-col gap-2">
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Segmentation</label>
          <Combobox
            items={segmentationOptions}
            value={selectedSegmentations}
            showClear
            searchable
            multiple
            maxChips="auto"
            placeholder="Filter by Segmentation..."
            className="w-full bg-white"
            itemToStringLabel={item => item.text}
            itemToBadge={item => item.label.toUpperCase()}
            onValueChange={value => setSegmentationIds((value ?? []).map(option => Number(option.value)))}
          />
        </div>
      ) : null}

      <div className="flex flex-col gap-2">
        <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Source</label>
        <ReviewSourceFilterButtons variant="expandable" value={state.sourceSystem} onChange={setSourceSystem} />
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Rating</label>
        <CoachRatingFilterFields
          ratingType={state.ratingType}
          onRatingTypeChange={setRatingType}
          ratingValue={state.ratingValue}
          onRatingValueChange={setRatingValue}
          npsCategory={state.npsCategory}
          onNpsCategoryChange={setNpsCategory}
          recommendation={state.recommendation}
          onRecommendationChange={setRecommendation}
        />
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Sentiment</label>
        <ReviewSentimentFilterButtons variant="row" value={state.sentiment} onChange={setSentiment} />
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Reply Status</label>
        <ReviewTriageFilterButtons variant="row" value={state.triage} onChange={setTriage} />
      </div>

      {canSeeArchived ? (
        <div className="flex flex-col gap-2">
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Show</label>
          <div className="grid grid-cols-2 gap-1.5 w-full" role="group" aria-label="Show">
            {(
              [
                { archived: false, label: "Active" },
                { archived: true, label: "Archived" },
              ] as const
            ).map(option => {
              const selected = state.archived === option.archived;
              return (
                <button
                  key={option.label}
                  type="button"
                  aria-pressed={selected}
                  className={cn(
                    "rounded-lg border px-2 py-2 text-xs font-semibold transition-colors cursor-pointer",
                    selected
                      ? "bg-slate-700 border-slate-700 text-white"
                      : "bg-white border-slate-200 text-slate-500 hover:border-slate-300 hover:text-slate-700 hover:bg-slate-50"
                  )}
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    setArchived(option.archived);
                  }}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      <ReviewTimeFrameFilter
        preset={state.datePreset}
        startDate={state.startDate}
        endDate={state.endDate}
        onChange={setDatePreset}
      />
    </ReviewFiltersPopoverLayout>
  );
}
